/**
 * IncomingCallOverlay — fullscreen accept/decline prompt for incoming calls.
 *
 * Shown when another user invites you to a call via Momento event.
 * Accept → joins the Huddle01 room
 * Decline → dismisses, no action taken on the caller's side
 */

import { useEffect, useRef } from "react";
import { Phone, PhoneOff, Video } from "lucide-react";
import { cn } from "@slumbr/ui";
import { useMessengerStore } from "../stores/messenger-store";

export function IncomingCallOverlay() {
  const incomingCall = useMessengerStore((s) => s.incomingCall);
  const acceptIncomingCall = useMessengerStore((s) => s.acceptIncomingCall);
  const declineIncomingCall = useMessengerStore((s) => s.declineIncomingCall);
  const acceptingRef = useRef(false);

  // Auto-dismiss after 30 seconds (missed call)
  useEffect(() => {
    if (!incomingCall) return;
    const timer = setTimeout(() => {
      declineIncomingCall();
    }, 30_000);
    return () => clearTimeout(timer);
  }, [incomingCall, declineIncomingCall]);

  if (!incomingCall) return null;

  const isVideo = incomingCall.type === "video";

  async function handleAccept() {
    if (acceptingRef.current) return;
    acceptingRef.current = true;
    await acceptIncomingCall();
    acceptingRef.current = false;
  }

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="mx-4 w-full max-w-sm overflow-hidden rounded-2xl bg-surface-0 shadow-2xl dark:bg-surface-dark-1">
        {/* Animated ring header */}
        <div className="flex flex-col items-center bg-gradient-to-b from-brand-500/10 to-transparent px-6 pb-6 pt-8">
          {/* Avatar / icon */}
          <div className="relative mb-4">
            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-brand-500/10 ring-4 ring-brand-500/20">
              <span className="text-3xl font-bold text-brand-500">
                {incomingCall.callerName.slice(0, 2).toUpperCase()}
              </span>
            </div>
            {/* Pulse rings */}
            <div className="absolute inset-0 rounded-full ring-4 ring-brand-500/30 animate-ping" />
          </div>

          <p className="text-[11px] font-medium uppercase tracking-widest text-gray-500 dark:text-gray-400">
            Incoming {isVideo ? "video" : "voice"} call
          </p>
          <h2 className="mt-1 text-xl font-semibold text-gray-900 dark:text-gray-100">
            {incomingCall.callerName}
          </h2>
        </div>

        {/* Accept / Decline buttons */}
        <div className="flex items-center justify-around px-8 pb-8">
          {/* Decline */}
          <div className="flex flex-col items-center gap-2">
            <button
              onClick={declineIncomingCall}
              className="flex h-16 w-16 items-center justify-center rounded-full bg-red-500 text-white shadow-lg shadow-red-500/30 transition-transform active:scale-95 hover:bg-red-600"
            >
              <PhoneOff className="h-7 w-7" />
            </button>
            <span className="text-xs font-medium text-gray-500">Decline</span>
          </div>

          {/* Accept */}
          <div className="flex flex-col items-center gap-2">
            <button
              onClick={() => { void handleAccept(); }}
              className={cn(
                "flex h-16 w-16 items-center justify-center rounded-full text-white shadow-lg transition-transform active:scale-95",
                "bg-green-500 shadow-green-500/30 hover:bg-green-600"
              )}
            >
              {isVideo ? (
                <Video className="h-7 w-7" />
              ) : (
                <Phone className="h-7 w-7" />
              )}
            </button>
            <span className="text-xs font-medium text-gray-500">Accept</span>
          </div>
        </div>
      </div>
    </div>
  );
}
